/** Decides WHICH message the Subscribe page shows after a store purchase or a
 *  Restore Purchases tap. The rule is here rather than in the page so it can be
 *  unit-tested without bootstrapping the component, mirroring its sibling
 *  [subscribe-restore-error-filter.ts](./subscribe-restore-error-filter.ts).
 *
 *  Why it exists (#4651). The page used to show one terminal "Subscription
 *  Purchase Failed" dialog for every failure. That sentence is false whenever
 *  the store already completed and charged the purchase and it was OUR
 *  confirmation step that failed — which is the dialog an Apple App Review
 *  reviewer photographed when they rejected build 1.31.8 (60) on 2026-08-08.
 *  Telling somebody their payment failed after they have paid invites them to
 *  pay again.
 *
 *  The distinction it rests on, which the server side established in #4649:
 *  a store that SETTLED the question (no such transaction, expired, owned by
 *  somebody else) has told us the purchase is not valid, and saying so is
 *  correct. A store we could not reach has told us nothing at all. The full
 *  reasoning is in `design-docs/iap-subscriptions.md`.
 */

import { STORE_VALIDATE_ACCOUNT_MISMATCH, STORE_VALIDATE_REQUEST_REFUSED } from '@cf-shared/store-validate-errors';

/** Which of the Subscribe page's messages an outcome should produce. */
export enum PurchaseOutcomeMessage {
  /** Show nothing: it worked, or the user backed out of the store sheet. */
  None,
  /** The store subscription belongs to a different app account. The existing
   *  dialog names that account and offers to sign out. */
  AlreadyLinked,
  /** The store's own record says a different one of our accounts started this
   *  purchase, and no account we can name holds it (#5384). Usually an account
   *  the buyer since deleted. Say where the purchase belongs and point at
   *  support, which can move an Apple purchase to this account. */
  OtherAccount,
  /** The store refused our confirmation request itself (#6239), and it refuses
   *  the same purchase token every time (#6243). Asking again cannot help, so
   *  point a buyer who believes they paid at support. Never say the purchase
   *  is invalid: the refusal says nothing about whether one exists. */
  StoreRefused,
  /** The buyer was charged and we could not confirm it. Say the purchase went
   *  through and will activate shortly — never that it failed. */
  ConfirmationPending,
  /** The purchase genuinely did not happen, or the store settled it as not
   *  valid. The existing terminal dialog is correct here. */
  Failed,
}

/** The parts of `IAPPurchaseResult` this rule reads. Declared here as plain
 *  fields rather than imported, so this module stays free of the Angular
 *  service that owns that interface and the test needs no dependency
 *  injection. */
export interface PurchaseOutcome {
  success: boolean;
  canceled?: boolean;
  error?: string;
  /** Set by `IAPService` only where it can prove the store reported a
   *  completed, paid transaction BEFORE the failure recorded in `error`. */
  confirmationFailedAfterPurchase?: boolean;
}

export function decidePurchaseOutcomeMessage(outcome: PurchaseOutcome): PurchaseOutcomeMessage {
  // if it worked, or the user chose to back out, there is nothing to say
  if (outcome.success) return PurchaseOutcomeMessage.None;
  if (outcome.canceled) return PurchaseOutcomeMessage.None;

  // if the subscription belongs to another account, that dialog wins even when
  // the buyer was also charged: it can name the owning account, which the
  // confirmation-pending message cannot, and signing out is the way forward.
  if (outcome.error === 'already_linked') return PurchaseOutcomeMessage.AlreadyLinked;

  // if the store says another of our accounts started it, "failed" would be
  // false and "try again" pointless: the store's record will not change. This
  // wins over the confirmation-pending message too, because the server has
  // answered and the answer is final.
  if (outcome.error === STORE_VALIDATE_ACCOUNT_MISMATCH) return PurchaseOutcomeMessage.OtherAccount;

  // if the store refused the request itself, "try again" is advice that cannot
  // work: it refuses that token every time. Final for the same reason as above.
  if (outcome.error === STORE_VALIDATE_REQUEST_REFUSED) return PurchaseOutcomeMessage.StoreRefused;

  // if the buyer was charged and our confirmation never got an answer, promise
  // the activation rather than declaring a failure. The flag is the whole
  // condition on purpose — matching on the error text instead would promise an
  // activation to somebody who was never charged.
  if (outcome.confirmationFailedAfterPurchase) return PurchaseOutcomeMessage.ConfirmationPending;

  return PurchaseOutcomeMessage.Failed;
}
